import React, { useState, useEffect, useMemo, useContext } from 'react';
import '../css/CustomerSalesDetail.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AuthContext } from '../context/AuthContext';
import { salesAPI, returnsAPI, clientsAPI, getCompanyAccounts, getProducts } from '../utils/api';
import SearchableDropdown from '../components/SearchableDropdown';

const SalesTransactionsTable = ({
    customerId,
    customerName,
    formatDate,
    formatCurrency,
    transactions: propTransactions, // Accept transactions from parent
    filteredTransactions: propFilteredTransactions, // Accept filtered transactions from parent
    fromDate: propFromDate,
    toDate: propToDate,
    setFromDate: propSetFromDate,
    setToDate: propSetToDate,
    onFetch, // New prop for parent-controlled fetch
    filterType,
    setFilterType
}) => {
    const { user } = useContext(AuthContext);
    const role = user?.role?.toLowerCase();
    /* ===================== STATE ===================== */
    // Use props if available, otherwise default to empty (or internal state if we were fully hybrid, but we want to force props usage if provided)
    const [internalTransactions, setInternalTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Determine which transactions to use: Props > Internal
    // Priority: Filtered Props > Raw Props > Internal
    const transactions = propFilteredTransactions || propTransactions || internalTransactions;

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Selection state
    const [selectedIds, setSelectedIds] = useState([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingTx, setEditingTx] = useState(null);
    const [editForm, setEditForm] = useState({
        transaction_date: '',
        reference_number: '',
        transaction_type: '',
        product: '',
        quantity: '',
        unit_price: '',
        discount: '',
        with_gst: false,
        total_amount: 0,
        amount_paid: 0,
        account_paid: ''
    });
    const [companyAccounts, setCompanyAccounts] = useState([]);
    const [products, setProducts] = useState([]);
    const [productFilter, setProductFilter] = useState('');

    /* ===================== FETCH TRANSACTIONS ===================== */
    const fetchTransactions = async () => {
        // If onFetch prop is provided (Parent Controller), use it
        if (onFetch) {
            onFetch();
            return;
        }

        // If props are provided but no onFetch, do NOT fetch internally to avoid desync
        if (propTransactions) {
            // console.log('SalesTransactionsTable: Using passed props, skipping internal fetch.');
            return;
        }

        try {
            setLoading(true);

            // Parallel fetch: Sales + Returns
            const [salesRes, returnsRes] = await Promise.all([
                salesAPI.getCustomerSales(
                    customerId,
                    fromDate || undefined,
                    toDate || undefined
                ),
                returnsAPI.getAll({
                    client_id: customerId,
                    client_type: 'Customer',
                    startDate: fromDate || undefined,
                    endDate: toDate || undefined
                })
            ]);

            const salesDataRaw = salesRes.data.sales || [];
            // Hide auto-generated payment records from the sales transaction table
            const salesData = salesDataRaw.filter(txn => {
                if ((txn.transaction_type || '').toLowerCase() === 'payment' &&
                    txn.product && txn.product.startsWith('Payment - ')) {
                    return false;
                }
                return true;
            });
            const returnsData = returnsRes.data || [];

            // Normalize Returns to match Sales structure
            const normalizedReturns = returnsData.map(ret => ({
                id: `RET-${ret.id}`, // Unique ID prefix
                transaction_date: ret.date || ret.date_initiated,
                reference_number: ret.return_number || ret.return_id,
                transaction_type: 'return', // Mark as return
                product: ret.product || ret.reason || 'Return',
                quantity: ret.quantity || 0,
                unit_price: ret.unit_price || 0,
                discount: ret.discount || 0,
                with_gst: ret.with_gst,
                total_amount: ret.refund_amount || ret.total_amount || 0, // This is the credit amount
                amount_paid: ret.amount_paid || 0,
                balance: (parseFloat(ret.refund_amount || ret.total_amount || 0) - parseFloat(ret.amount_paid || 0)).toFixed(2)
            }));

            // Combine and Sort by Date DESC
            const combinedData = [...salesData, ...normalizedReturns].sort((a, b) => {
                return new Date(b.transaction_date) - new Date(a.transaction_date);
            });

            setInternalTransactions(combinedData);

            // Reset to first page when new data is fetched
            setCurrentPage(1);
        } catch (err) {
            // console.error('Error fetching transactions:', err);
        } finally {
            setLoading(false);
        }
    };

    /* ===================== INITIAL LOAD ===================== */
    useEffect(() => {
        if (customerId) {
            fetchTransactions();
            fetchCompanyAccounts();
        }
        fetchProductsList();
    }, [customerId]);

    const fetchProductsList = async () => {
        try {
            const data = await getProducts();
            setProducts(Array.isArray(data) ? data.map(p => p.name) : []);
        } catch (error) {
            // console.error('Error fetching products:', error);
        }
    };

    const fetchCompanyAccounts = async () => {
        try {
            const accounts = await getCompanyAccounts();
            setCompanyAccounts(accounts || []);
        } catch (error) {
            // console.error('Error fetching company accounts:', error);
        }
    };

    /* ===================== FILTER LOGIC ===================== */
    const filteredTransactionsData = useMemo(() => {
        return transactions.filter(txn => {
            // Product Filter (Exact Match)
            if (productFilter) {
                const searchLower = productFilter.toLowerCase();
                const matchesParent = txn.product?.toLowerCase() === searchLower;
                const matchesItems = txn.items?.some(item => (item.product_name || item.product)?.toLowerCase() === searchLower);

                if (!matchesParent && !matchesItems) {
                    return false;
                }
            }
            return true;
        });
    }, [transactions, productFilter]);

    /* ===================== PAGINATION LOGIC ===================== */
    // Calculate paginated data
    const paginatedTransactions = useMemo(() => {
        const indexOfLastRow = currentPage * rowsPerPage;
        const indexOfFirstRow = indexOfLastRow - rowsPerPage;
        return filteredTransactionsData.slice(indexOfFirstRow, indexOfLastRow);
    }, [filteredTransactionsData, currentPage, rowsPerPage]);

    // Calculate total pages
    const totalPages = Math.ceil(filteredTransactionsData.length / rowsPerPage);

    // Generate page numbers to display
    const getPageNumbers = () => {
        const pageNumbers = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            // Show all pages if total pages are less than or equal to maxVisiblePages
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            // Show limited pages with ellipsis
            if (currentPage <= 3) {
                // Near the beginning
                pageNumbers.push(1, 2, 3, 4, '...', totalPages);
            } else if (currentPage >= totalPages - 2) {
                // Near the end
                pageNumbers.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                // In the middle
                pageNumbers.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
            }
        }

        return pageNumbers;
    };

    // Pagination handlers
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages && page !== currentPage) {
            setCurrentPage(page);
        }
    };

    const goToFirstPage = () => goToPage(1);
    const goToLastPage = () => goToPage(totalPages);
    const goToNextPage = () => goToPage(currentPage + 1);
    const goToPreviousPage = () => goToPage(currentPage - 1);

    /* ===================== SELECTION HANDLERS ===================== */
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedIds(paginatedTransactions.map(t => t.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectRow = (id) => {
        setSelectedIds(prev =>
            prev.includes(id)
                ? prev.filter(item => item !== id)
                : [...prev, id]
        );
    };

    const handleBulkDelete = async () => {
        if (!selectedIds.length) return;

        if (window.confirm(`Are you sure you want to delete ${selectedIds.length} selected transaction(s)?`)) {
            try {
                setLoading(true);
                for (const id of selectedIds) {
                    if (id.toString().startsWith('RET-')) {
                        await returnsAPI.delete(id.replace('RET-', ''));
                    } else {
                        const transactionId = id.toString().replace(/^tx-/i, ''); // Preserve UUIDs
                        await clientsAPI.deleteTransaction(transactionId);
                    }
                }
                setSelectedIds([]);
                if (onFetch) onFetch();
                else fetchTransactions();
                alert('Transactions deleted successfully');
            } catch (err) {
                // console.error('Bulk delete error:', err);
                alert('Failed to delete some transactions');
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEditClick = () => {
        if (selectedIds.length !== 1) return;
        const tx = transactions.find(t => t.id === selectedIds[0]);
        if (tx) {
            setEditingTx(tx);
            setEditForm({
                transaction_date: tx.transaction_date ? new Date(tx.transaction_date).toISOString().split('T')[0] : '',
                reference_number: tx.reference_number || '',
                transaction_type: tx.transaction_type || '',
                product: tx.product || '',
                quantity: tx.quantity || 0,
                unit_price: tx.unit_price || 0,
                discount: tx.discount || 0,
                with_gst: tx.with_gst || false,
                total_amount: tx.total_amount || 0,
                amount_paid: tx.amount_paid || 0,
                account_paid: tx.account_paid || '',
                items: (tx.items && tx.items.length > 0)
                    ? tx.items.map(i => ({
                        product: i.product_name || i.product || '',
                        quantity: Number(i.quantity) || 0,
                        unit_price: Number(i.unit_price) || 0,
                        with_gst: Boolean(i.with_gst)
                    }))
                    : [{
                        product: tx.product || '',
                        quantity: Number(tx.quantity) || 0,
                        unit_price: Number(tx.unit_price) || 0,
                        with_gst: Boolean(tx.with_gst)
                    }]
            });
            setShowEditModal(true);
        }
    };

    const handleSaveEdit = async () => {
        try {
            setLoading(true);
            if (editingTx.id === 'opening-balance') {
                await clientsAPI.updateOpeningBalancePaid(
                    customerId,
                    { ...editForm }
                );
            } else {
                if (editingTx.id.toString().startsWith('RET-')) {
                    // It's a return transaction
                    const returnId = editingTx.id.replace('RET-', '');
                    await returnsAPI.update(returnId, {
                        ...editForm,
                    });
                } else {
                    // 1. Update the original transaction details
                    const transactionId = editingTx.id.toString().replace(/^tx-/i, '');
                    await clientsAPI.updateTransaction(
                        transactionId,
                        {
                            ...editForm,
                        }
                    );
                }
            }
            setShowEditModal(false);
            setSelectedIds([]);
            if (onFetch) onFetch();
            else fetchTransactions();
            alert('Transaction updated successfully');
        } catch (err) {
            // console.error('Update error:', err);
            alert('Failed to update transaction');
        } finally {
            setLoading(false);
        }
    };

    const recalcEditTotal = (form) => {
        let totalSubtotal = 0;
        let totalGST = 0;

        if (form.items && form.items.length > 0) {
            form.items.forEach(item => {
                const qty = Number(item.quantity) || 0;
                const price = Number(item.unit_price) || 0;
                const itemAmount = qty * price;
                totalSubtotal += itemAmount;

                if (item.with_gst) {
                    totalGST += itemAmount * 0.18;
                }
            });
        }

        const discount = Number(form.discount) || 0;
        let grandTotal = (totalSubtotal + totalGST) - discount;
        if (grandTotal < 0) grandTotal = 0;

        form.total_amount = Math.round(grandTotal);
        return form;
    };

    const handleItemChange = (index, field, value) => {
        setEditForm(prev => {
            const newItems = [...(prev.items || [])];
            newItems[index] = { ...newItems[index], [field]: value };
            return recalcEditTotal({ ...prev, items: newItems });
        });
    };

    const addItem = () => {
        setEditForm(prev => ({
            ...prev,
            items: [...(prev.items || []), { product: '', quantity: 1, unit_price: 0, discount: 0, with_gst: false }]
        }));
    };

    const removeItem = (index) => {
        setEditForm(prev => {
            const newItems = (prev.items || []).filter((_, i) => i !== index);
            return recalcEditTotal({
                ...prev,
                items: newItems.length > 0 ? newItems : [{ product: '', quantity: 1, unit_price: 0, discount: 0, with_gst: false }]
            });
        });
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditForm(prev => {
            const newForm = {
                ...prev,
                [name]: type === 'checkbox' ? checked : value
            };

            if (name === 'discount') {
                return recalcEditTotal(newForm);
            }

            return newForm;
        });
    };

    /* ===================== HELPERS ===================== */
    const formatCurrencyWithoutSymbol = (amount) => {
        if (!amount) return '0.00';
        const num = parseFloat(amount);
        return num.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const formatDateDDMMYYYY = (date) => {
        const d = new Date(date);
        return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
    };

    /* ===================== PDF EXPORT ===================== */
    const exportToPDF = () => {
        if (!filteredTransactionsData.length) {
            alert('No transactions to export!');
            return;
        }

        try {
            const doc = new jsPDF('landscape');

            // ---------- HEADER ----------
            doc.setFontSize(24);
            doc.setFont('helvetica', 'bold');
            doc.setTextColor(0, 0, 128);
            doc.text('OMEGA', 14, 20);

            doc.setFontSize(16);
            doc.setTextColor(0, 0, 0);
            doc.text('Customer Sales Transaction Details', 14, 30);

            doc.setDrawColor(0, 0, 128);
            doc.line(14, 35, 280, 35);

            doc.setFontSize(11);
            doc.setFont('helvetica', 'bold');
            doc.text('Client:', 14, 45);
            doc.setFont('helvetica', 'normal');
            doc.text(customerName || 'N/A', 30, 45);

            doc.setFont('helvetica', 'bold');
            doc.text('Customer ID:', 90, 45);
            doc.setFont('helvetica', 'normal');
            doc.text(customerId, 115, 45);

            doc.setFont('helvetica', 'bold');
            doc.text('Report Date:', 200, 45);
            doc.setFont('helvetica', 'normal');
            doc.text(formatDateDDMMYYYY(new Date()), 225, 45);

            let yPos = 55;
            if (fromDate || toDate) {
                doc.setFont('helvetica', 'bold');
                doc.text('Period:', 14, yPos);
                doc.setFont('helvetica', 'normal');
                doc.text(`${fromDate || 'Start'} to ${toDate || 'End'}`, 30, yPos);
                yPos += 10;
            }

            // ---------- TABLE DATA ----------
            const tableData = filteredTransactionsData.map(txn => {
                const hasItems = txn.items && txn.items.length > 0;

                let productText = txn.product || '-';
                let quantityText = (txn.transaction_type === 'opening' ? '-' : (txn.quantity || '-')).toString();
                let unitPriceText = txn.transaction_type === 'opening' ? '-' : formatCurrencyWithoutSymbol(txn.unit_price || 0);

                if (hasItems) {
                    // Filter items if product filter is active (exact match)
                    const displayItems = productFilter
                        ? txn.items.filter(item => (item.product_name || item.product)?.toLowerCase() === productFilter.toLowerCase())
                        : txn.items;

                    productText = displayItems.map(item => `• ${item.product_name || item.product}`).join('\n');
                    quantityText = displayItems.map(item => item.quantity).join('\n');
                    unitPriceText = displayItems.map(item => formatCurrencyWithoutSymbol(item.unit_price || 0)).join('\n');
                }

                return [
                    formatDate(txn.transaction_date),
                    txn.reference_number || '-',
                    txn.transaction_type?.toUpperCase() || '-',
                    productText,
                    quantityText,
                    unitPriceText,
                    txn.transaction_type === 'opening' ? '-' : formatCurrencyWithoutSymbol(txn.discount || 0),
                    txn.transaction_type === 'opening' ? '-' : (txn.with_gst ? 'Yes' : 'No'),
                    formatCurrencyWithoutSymbol(txn.total_amount || 0),
                    formatCurrencyWithoutSymbol(txn.amount_paid || 0),
                    txn.account_paid || '-',
                    txn.transaction_type === 'return'
                        ? formatCurrencyWithoutSymbol(-(parseFloat(txn.total_amount || 0) - parseFloat(txn.amount_paid || 0)))
                        : formatCurrencyWithoutSymbol(parseFloat(txn.total_amount || 0) - parseFloat(txn.amount_paid || 0))
                ];
            });

            // Calculate totals for summary
            const totals = {
                total_amount: filteredTransactionsData.reduce((s, t) => {
                    // For summary 'Total Amt', we usually want the sum of invoices/returns only
                    if (['sale', 'opening', 'return'].includes((t.transaction_type || '').toLowerCase())) {
                        return s + (+t.total_amount || 0);
                    }
                    return s;
                }, 0),
                amount_paid: filteredTransactionsData.reduce((s, t) => s + (+t.amount_paid || 0), 0),
            };

            // Balance should match the 'Current Balance' card logic:
            // Sum of (Invoices - Returns) - Sum of All Payments
            // Simplified: Sum of all (total_amount - amount_paid) but for returns total_amount is negative in balance context
            const totalBalance = filteredTransactionsData.reduce((s, t) => {
                const type = (t.transaction_type || '').toLowerCase();
                const amt = parseFloat(t.total_amount || 0);
                const paid = parseFloat(t.amount_paid || 0);

                if (type === 'return') {
                    return s - (amt - paid);
                } else if (['sale', 'opening'].includes(type)) {
                    return s + (amt - paid);
                } else if (type === 'payment') {
                    // For a payment row, total_amount and amount_paid are usually same.
                    // We only want to subtract the payment.
                    return s - paid;
                }
                return s;
            }, 0);

            autoTable(doc, {
                startY: yPos + 5,
                head: [[
                    'Date', 'Invoice No', 'Type', 'Product', 'Qty',
                    'Unit Price', 'Discount', 'GST',
                    'Total Amt', 'Paid', 'Account Paid', 'Balance'
                ]],
                body: tableData,
                styles: { fontSize: 8, cellPadding: 3 },
                headStyles: { fillColor: [0, 0, 128], textColor: 255 },
                columnStyles: {
                    0: { cellWidth: 20 },
                    1: { cellWidth: 24 },
                    2: { cellWidth: 16 },
                    3: { cellWidth: 32 },
                    4: { cellWidth: 12, halign: 'right' },
                    5: { cellWidth: 20, halign: 'right' },
                    6: { cellWidth: 16, halign: 'right' },
                    7: { cellWidth: 12 },
                    8: { cellWidth: 22, halign: 'right' },
                    9: { cellWidth: 22, halign: 'right' },
                    10: { cellWidth: 24 },
                    11: { cellWidth: 24, halign: 'right' }
                },
                margin: { left: 14, right: 14 }
            });

            // ---------- SUMMARY (CORRECT WAY) ----------
            const finalY = doc.lastAutoTable.finalY;
            const summaryY = finalY + 10;

            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.text(`Total Txn: ${filteredTransactionsData.length}`, 40, summaryY);
            doc.text(`Total Amt: ${formatCurrencyWithoutSymbol(totals.total_amount)}`, 90, summaryY);
            doc.text(`Paid: ${formatCurrencyWithoutSymbol(totals.amount_paid)}`, 150, summaryY);
            doc.text(`Balance: ${formatCurrencyWithoutSymbol(totalBalance)}`, 210, summaryY);

            // ---------- FOOTER ----------
            doc.setFontSize(9);
            doc.setFont('helvetica', 'italic');
            doc.text(
                'End of Report • Generated by Omega Sales System',
                14,
                summaryY + 12
            );

            const dateStr = formatDateDDMMYYYY(new Date());
            const sanitizedName = (customerName || 'Customer').replace(/[^a-zA-Z0-9]/g, '_');
            doc.save(`Sales_Transaction_${sanitizedName}_${dateStr}.pdf`);

        } catch (error) {
            // console.error('PDF Error:', error);
            alert('Failed to generate PDF');
        }
    };

    /* ===================== UI ===================== */
    return (
        <div className="transactions-table-container">
            <div className="table-header">
                <h3>Transaction History</h3>
                <span>
                    {loading ? 'Loading...' : `Showing ${paginatedTransactions.length} of ${transactions.length} entries`}
                </span>
            </div>

            <div className="table-filters">
                <div className="filter-card">
                    <div className="filter-item">
                        <label>From Date</label>
                        <input
                            type="date"
                            className="filter-input"
                            value={propFromDate !== undefined ? propFromDate : fromDate}
                            onChange={e => (propSetFromDate || setFromDate)(e.target.value)}
                        />
                    </div>
                    <div className="filter-item">
                        <label>To Date</label>
                        <input
                            type="date"
                            className="filter-input"
                            value={propToDate !== undefined ? propToDate : toDate}
                            onChange={e => (propSetToDate || setToDate)(e.target.value)}
                        />
                    </div>
                    <div className="filter-item">
                        <label>Transaction Type</label>
                        <select
                            className="filter-input"
                            value={filterType || 'all'}
                            onChange={e => setFilterType && setFilterType(e.target.value)}
                        >
                            <option value="all">All Types</option>
                            <option value="SALE">Sales Invoice</option>
                            <option value="payment">Payment Received</option>
                            <option value="CASH">Cash</option>
                            <option value="BANK_TRANSFER">Bank Transfer</option>
                            <option value="CHEQUE">Cheque</option>
                            <option value="UPI">UPI</option>
                            <option value="RETURN">Return</option>
                            <option value="OPENING">Opening Balance</option>
                        </select>
                    </div>

                    <div className="filter-item">
                        <label>Product</label>
                        <select
                            className="filter-input"
                            value={productFilter}
                            onChange={(e) => setProductFilter(e.target.value)}
                        >
                            <option value="">All Products</option>
                            {products.map((p, i) => (
                                <option key={i} value={p}>{p}</option>
                            ))}
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>&nbsp;</label>
                        <button
                            className="btn btn-fetch"
                            onClick={fetchTransactions}
                            disabled={loading}
                        >
                            {loading ? 'Fetching...' : 'Fetch'}
                        </button>
                    </div>
                    <div className="filter-actions">
                        <button
                            className="btn btn-clear"
                            onClick={() => {
                                if (propSetFromDate) propSetFromDate('');
                                else setFromDate('');

                                if (propSetToDate) propSetToDate('');
                                else setToDate('');

                                if (setFilterType) setFilterType('all');
                                setProductFilter('');

                                setTimeout(() => fetchTransactions(), 100);
                            }}
                        >
                            Clear
                        </button>
                        <button
                            className="btn btn-export"
                            onClick={exportToPDF}
                            disabled={transactions.length === 0}
                        >
                            Export PDF
                        </button>
                    </div>
                </div>

                {role !== 'normal_user' && (
                    <div className="action-bar mb-3 d-flex gap-2">
                        <button
                            className="btn btn-primary"
                            disabled={selectedIds.length !== 1}
                            onClick={handleEditClick}
                        >
                            <i className="bi bi-pencil"></i> Edit Transaction
                        </button>
                        <button
                            className="btn btn-danger"
                            disabled={selectedIds.length === 0}
                            onClick={handleBulkDelete}
                        >
                            <i className="bi bi-trash"></i> Delete Selected ({selectedIds.length})
                        </button>
                    </div>
                )}
            </div>

            <div className="table-responsive">
                <table className="transactions-table table table-striped table-bordered table-hover">
                    <thead className="table-dark">
                        <tr>
                            <th style={{ width: '40px' }} className="text-center">
                                <input
                                    type="checkbox"
                                    onChange={handleSelectAll}
                                    checked={paginatedTransactions.length > 0 && selectedIds.length === paginatedTransactions.length}
                                />
                            </th>
                            <th>Date</th>
                            <th>Invoice No</th>
                            <th>Type</th>
                            <th>Product</th>
                            <th>Qty</th>
                            <th>Unit Price</th>
                            <th>Discount</th>
                            <th>GST</th>
                            <th>Total Amount</th>
                            <th>Amount Paid</th>
                            <th>Account Paid</th>
                            <th>Balance</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="12" className="text-center py-4">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                    <div className="mt-2">Loading transactions...</div>
                                </td>
                            </tr>
                        ) : paginatedTransactions.length === 0 ? (
                            <tr>
                                <td colSpan="12" className="text-center py-4">
                                    No transactions found
                                </td>
                            </tr>
                        ) : paginatedTransactions.map(txn => (
                            <><tr key={txn.id} className={selectedIds.includes(txn.id) ? 'table-primary' : ''}>
                                <td className="text-center">
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.includes(txn.id)}
                                        onChange={() => handleSelectRow(txn.id)}
                                    />
                                </td>
                                <td>{formatDate(txn.transaction_date)}</td>
                                <td>{txn.reference_number || '-'}</td>
                                <td>
                                    <span className={`badge ${txn.transaction_type === 'sale' ? 'bg-success' :
                                        txn.transaction_type === 'return' ? 'bg-danger' : 'bg-warning'}`}>
                                        {txn.transaction_type?.toUpperCase()}
                                    </span>
                                    {txn.transaction_type === 'payment' && txn.product && txn.product.includes('Payment -') && (
                                        <div style={{ fontSize: '0.8em', color: '#666', marginTop: '2px' }}>
                                            {txn.product.replace('Payment - ', '')}
                                        </div>
                                    )}
                                </td>
                                <td>
                                    {txn.items && txn.items.length > 0 ? (
                                        <div style={{ fontSize: '0.9em' }}>
                                            {(productFilter
                                                ? txn.items.filter(item => (item.product_name || item.product)?.toLowerCase() === productFilter.toLowerCase())
                                                : txn.items
                                            ).map((item, i) => (
                                                <div key={i} style={{ marginBottom: '4px', whiteSpace: 'nowrap' }}>
                                                    • {item.product_name || item.product}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        txn.product
                                    )}
                                </td><td className="text-end">
                                    {txn.items && txn.items.length > 0 ? (
                                        <div style={{ fontSize: '0.9em' }}>
                                            {(productFilter
                                                ? txn.items.filter(item => (item.product_name || item.product)?.toLowerCase() === productFilter.toLowerCase())
                                                : txn.items
                                            ).map((item, i) => (
                                                <div key={i} style={{ marginBottom: '4px' }}>
                                                    {item.quantity}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        txn.quantity
                                    )}
                                </td><td className="text-end">
                                    {txn.items && txn.items.length > 0 ? (
                                        <div style={{ fontSize: '0.9em' }}>
                                            {(productFilter
                                                ? txn.items.filter(item => (item.product_name || item.product)?.toLowerCase() === productFilter.toLowerCase())
                                                : txn.items
                                            ).map((item, i) => (
                                                <div key={i} style={{ marginBottom: '4px' }}>
                                                    {formatCurrency(item.unit_price)}
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        formatCurrency(txn.unit_price)
                                    )}
                                </td>
                                <td className="text-end">{formatCurrency(txn.discount)}</td><td className="text-center">
                                    {txn.with_gst ?
                                        <span className="badge bg-success">Yes</span> :
                                        <span className="badge bg-secondary">No</span>}
                                </td>
                                <td className="text-end">{formatCurrency(txn.total_amount)}</td>
                                <td className="text-end">{formatCurrency(txn.amount_paid)}</td>
                                <td className="text-end">{txn.account_paid || '-'}</td>
                                <td className={`text-end fw-bold ${(parseFloat(txn.total_amount || 0) - parseFloat(txn.amount_paid || 0)) > 0 ? 'text-danger' : 'text-success'}`}>
                                    {txn.transaction_type === 'return'
                                        ? formatCurrency(-(parseFloat(txn.total_amount || 0) - parseFloat(txn.amount_paid || 0)))
                                        : formatCurrency(parseFloat(txn.total_amount || 0) - parseFloat(txn.amount_paid || 0))
                                    }
                                </td>
                            </tr >
                            </>
                        ))}

                    </tbody >
                </table>
            </div>


            {/* Pagination Section - Shows when we have data */}
            {
                transactions.length > 0 && (
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 p-3 border-top">
                        {/* Page Info */}
                        <div className="text-muted mb-2 mb-md-0">
                            Showing <strong>{((currentPage - 1) * rowsPerPage) + 1}</strong> to <strong>{Math.min(currentPage * rowsPerPage, filteredTransactionsData.length)}</strong> of <strong>{filteredTransactionsData.length}</strong> entries
                            {totalPages > 1 && <span className="ms-2">(Page {currentPage} of {totalPages})</span>}
                        </div>

                        {/* Pagination Controls - Only show if more than one page */}
                        {totalPages > 1 && (
                            <nav aria-label="Transaction pagination" className="mb-2 mb-md-0">
                                <ul className="pagination mb-0">
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={goToFirstPage}
                                            disabled={currentPage === 1}
                                            aria-label="First Page"
                                        >
                                            &laquo;
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={goToPreviousPage}
                                            disabled={currentPage === 1}
                                            aria-label="Previous Page"
                                        >
                                            &lsaquo;
                                        </button>
                                    </li>

                                    {getPageNumbers().map((page, index) => (
                                        <React.Fragment key={index}>
                                            {page === '...' ? (
                                                <li className="page-item disabled">
                                                    <span className="page-link">...</span>
                                                </li>
                                            ) : (
                                                <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                                    <button
                                                        className="page-link"
                                                        onClick={() => goToPage(page)}
                                                    >
                                                        {page}
                                                    </button>
                                                </li>
                                            )}
                                        </React.Fragment>
                                    ))}

                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={goToNextPage}
                                            disabled={currentPage === totalPages}
                                            aria-label="Next Page"
                                        >
                                            &rsaquo;
                                        </button>
                                    </li>
                                    <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                        <button
                                            className="page-link"
                                            onClick={goToLastPage}
                                            disabled={currentPage === totalPages}
                                            aria-label="Last Page"
                                        >
                                            &raquo;
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        )}

                        {/* Rows per page selector */}
                        <div className="d-flex align-items-center">
                            <span className="text-muted me-2">Show:</span>
                            <select
                                className="form-select form-select-sm"
                                value={rowsPerPage}
                                onChange={(e) => {
                                    setRowsPerPage(Number(e.target.value));
                                    setCurrentPage(1); // Reset to first page
                                }}
                                style={{ width: 'auto' }}
                            >
                                <option value="5">5</option>
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                            </select>
                            <span className="text-muted ms-2">per page</span>
                        </div>
                    </div>
                )
            }

            {/* Mobile-friendly simple pagination */}
            {
                transactions.length > 0 && totalPages > 1 && (
                    <div className="d-md-none mt-3 border-top pt-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                ← Previous
                            </button>
                            <span className="text-muted fw-medium">
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={goToNextPage}
                                disabled={currentPage === totalPages}
                            >
                                Next →
                            </button>
                        </div>
                        <div className="text-center text-muted small">
                            {((currentPage - 1) * rowsPerPage) + 1}-{Math.min(currentPage * rowsPerPage, transactions.length)} of {transactions.length} entries
                        </div>
                    </div>
                )
            }
            {/* Edit Transaction Modal */}
            {showEditModal && editingTx && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Edit Transaction</h5>
                                <button type="button" className="btn-close" onClick={() => setShowEditModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Date</label>
                                        <input type="date" className="form-control" name="transaction_date" value={editForm.transaction_date} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Invoice No</label>
                                        <input type="text" className="form-control" name="reference_number" value={editForm.reference_number} onChange={handleChange} />
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <label className="form-label">Type</label>
                                        <input type="text" className="form-control" name="transaction_type" value={editForm.transaction_type} onChange={handleChange} />
                                    </div>

                                    {/* ITEMS LIST */}
                                    <div className="col-12 mb-3">
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <label className="form-label fw-bold mb-0">Items</label>
                                        </div>

                                        <div className="items-section">
                                            {/* Table Header */}
                                            <div className="items-header" style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(150px, 3fr) 80px 100px 100px 50px 40px',
                                                gap: '10px',
                                                marginBottom: '5px',
                                                padding: '0 10px',
                                                fontSize: '12px',
                                                fontWeight: 'bold',
                                                color: '#555'
                                            }}>
                                                <div>Product</div>
                                                <div>Qty</div>
                                                <div>Price (₹)</div>
                                                <div>Total (₹)</div>
                                                <div style={{ textAlign: 'center' }}>GST</div>
                                                <div></div>
                                            </div>

                                            {(editForm.items || []).map((item, index) => (
                                                <div key={index} className="item-row" style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: 'minmax(150px, 3fr) 80px 100px 100px 50px 40px',
                                                    gap: '10px',
                                                    alignItems: 'center',
                                                    marginBottom: '8px',
                                                    backgroundColor: '#fff',
                                                    border: '1px solid #eee',
                                                    padding: '8px 10px',
                                                    borderRadius: '6px'
                                                }}>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <SearchableDropdown
                                                            options={products}
                                                            value={item.product || ''}
                                                            onChange={(val) => handleItemChange(index, 'product', val)}
                                                            placeholder="Select Product"
                                                        />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input type="text" className="form-control form-control-sm" value={item.quantity || ''} placeholder="0" onChange={(e) => handleItemChange(index, 'quantity', e.target.value)} style={{ margin: 0 }} />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input type="text" className="form-control form-control-sm" value={item.unit_price || ''} placeholder="0.00" onChange={(e) => handleItemChange(index, 'unit_price', e.target.value)} style={{ margin: 0 }} />
                                                    </div>
                                                    <div style={{ marginBottom: 0 }}>
                                                        <input type="text" className="form-control form-control-sm bg-light fw-bold" value={((Number(item.quantity) || 0) * (Number(item.unit_price) || 0)).toFixed(2)} readOnly style={{ margin: 0 }} />
                                                    </div>
                                                    <div style={{ marginBottom: 0, textAlign: 'center' }}>
                                                        <input type="checkbox" checked={item.with_gst || false} onChange={(e) => handleItemChange(index, 'with_gst', e.target.checked)} style={{ transform: 'scale(1.2)', cursor: 'pointer' }} />
                                                    </div>
                                                    <div style={{ marginBottom: 0, textAlign: 'center' }}>
                                                        <button type="button" className="btn btn-sm text-danger p-0 border-0 bg-transparent fs-5" onClick={() => removeItem(index)} title="Remove Item">×</button>
                                                    </div>
                                                </div>
                                            ))}
                                            <button type="button" className="btn btn-sm btn-outline-secondary mt-2" onClick={addItem}>+ Add Another Item</button>
                                        </div>
                                    </div>

                                    <div className="col-md-6 mb-3">
                                        <label className="form-label">Global Discount (₹)</label>
                                        <input type="text" className="form-control" name="discount" value={editForm.discount} placeholder="0.00" onChange={handleChange} />
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label text-success fw-bold">Grand Total (₹)</label>
                                        <input type="text" className="form-control fw-bold text-success" name="total_amount" value={editForm.total_amount} readOnly />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                                <button type="button" className="btn btn-primary" onClick={handleSaveEdit} disabled={loading}>
                                    {loading ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default SalesTransactionsTable;